import { Lock, UserCheck, Dumbbell, Sparkles } from "lucide-react";

const FEATURES = [
  {
    icon: Lock,
    title: "Members Only",
    copy: "No crowds, no queues, no waiting for equipment. Just you and your coach.",
  },
  {
    icon: UserCheck,
    title: "By Appointment",
    copy: "Access is booked around your schedule — always focused, never rushed.",
  },
  {
    icon: Dumbbell,
    title: "Fully Equipped",
    copy: "Full rack, barbell, dumbbells, adjustable bench and conditioning gear.",
  },
  {
    icon: Sparkles,
    title: "Built For Focus",
    copy: "A quiet, intentional space designed to keep you locked in from warm-up to cool-down.",
  },
];

export const Studio = () => {
  return (
    <section
      id="studio"
      data-testid="studio-section"
      className="relative py-24 sm:py-32 bg-[#09090B] border-t border-zinc-800 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-14">
        <div className="flex items-center gap-3 mb-5">
          <span className="h-[2px] w-12 bg-[#D4AF37]" />
          <p className="font-display text-[#D4AF37] text-sm font-bold tracking-[0.3em] uppercase">
            The Gym Space
          </p>
        </div>
        <h2
          data-testid="studio-heading"
          className="font-display text-white font-black uppercase tracking-tight leading-[0.9] text-5xl sm:text-6xl lg:text-7xl max-w-4xl"
        >
          A private studio. <span className="text-[#D4AF37]">Zero</span> distractions.
        </h2>
      </div>

      {/* Full-bleed image with offset panel */}
      <div className="relative">
        <div className="relative h-[55vh] sm:h-[65vh] lg:h-[80vh] w-full overflow-hidden">
          <img
            src="https://customer-assets.emergentagent.com/job_je-fitness-hub/artifacts/3iskgyro_IMG_9407.webp"
            alt="Inside the JE Training private studio — squat rack, bench and free weights"
            className="h-full w-full object-cover object-center"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-transparent to-transparent pointer-events-none" />
          <div
            data-testid="studio-private-badge"
            className="absolute top-6 left-6 lg:top-8 lg:left-10 inline-flex items-center gap-2 bg-zinc-950/85 backdrop-blur-sm border border-[#D4AF37]/40 px-4 py-2.5"
          >
            <Lock size={14} strokeWidth={1.5} className="text-[#D4AF37]" />
            <span className="font-display text-xs font-bold tracking-[0.28em] uppercase text-white">
              Private Access · Braintree
            </span>
          </div>
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
            <div className="lg:col-span-7 relative lg:-mt-40 -mt-24 sm:-mt-32 bg-zinc-950 border border-zinc-800 p-8 lg:p-12">
              <p className="text-zinc-300 text-base lg:text-lg leading-relaxed font-light">
                Train out of a fully-kitted private studio in Braintree — no
                public gym distractions, no waiting for a rack, no audience.
                It's an exclusive environment reserved for JE Training clients
                only: switch off from the outside world and lock in on the work.
              </p>
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
                {FEATURES.map((f, i) => {
                  const Icon = f.icon;
                  return (
                    <div key={i} data-testid={`studio-feature-${i}`} className="flex gap-4">
                      <div className="h-10 w-10 shrink-0 border border-zinc-800 flex items-center justify-center">
                        <Icon size={17} strokeWidth={1.5} className="text-[#D4AF37]" />
                      </div>
                      <div>
                        <p className="font-display text-white text-base font-bold uppercase tracking-[0.08em]">
                          {f.title}
                        </p>
                        <p className="mt-1 text-zinc-500 text-sm leading-relaxed font-light">
                          {f.copy}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-10 pt-8 border-t border-zinc-800 flex flex-wrap items-center justify-between gap-5">
                <p className="font-display text-white text-2xl sm:text-3xl font-bold uppercase tracking-tight leading-none">
                  Your session. <span className="text-[#D4AF37]">Your space.</span>
                </p>
                <a
                  href="#contact"
                  data-testid="studio-cta"
                  className="inline-flex items-center gap-3 border border-[#D4AF37]/50 hover:border-[#D4AF37] hover:bg-[#D4AF37] hover:text-zinc-950 text-[#D4AF37] px-7 py-4 font-display text-base font-bold uppercase tracking-[0.16em] transition-colors duration-300"
                >
                  Enquire For Access
                </a>
              </div>
            </div>

            <div className="lg:col-span-5 relative" data-testid="studio-led-photo">
              <div className="relative aspect-[3/4] overflow-hidden border border-zinc-800">
                <img
                  src="/images/studio-led.webp"
                  alt="The JE Training studio after dark — LED-lit mirror, dumbbells and full rack"
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950/70 via-transparent to-transparent pointer-events-none" />
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-3">
                  <p className="font-display text-white text-lg font-bold uppercase tracking-[0.08em] leading-tight">
                    After dark<span className="text-[#D4AF37]">.</span>
                    <span className="block text-zinc-400 text-xs font-semibold tracking-[0.2em] mt-1">
                      Evening sessions, same intensity
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Studio;
